import { type PropsWithChildren, useReducer, useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';

import { Experiment, useOptimizely } from 'lib/sdks/optimizely';
import { useLocalStorage } from '@hooks/use-local-storage';

import {
  initialState,
  jobRefinementReducer,
  JobRefinementContext,
  INIT,
} from './JobRefinement.reducer';

import JobRefinementModal from './JobRefinementModal';
import JobSummaryModal from './JobSummaryModal';

import type {
  Address,
  AnsweredQuestion,
  Form,
  Category,
  RequestOrigin,
} from './types';
import { getTransformedJobBlob, getJobRefinementStorageName } from './utils';

import { track } from 'lib/sdks/analytics';

export * from './types';
export * from './JobRefinement.hook';

const nextPage = '/message/contact-information';

export const JobRefinementProvider = ({
  form,
  category,
  companyId,
  location,
  postcode,
  serviceId,
  serviceVersionId,
  children,
  forceLEQuestions,
  enableRAQOption,
  origin,
  onExitModal,
}: PropsWithChildren<{
  form: Form;
  category?: Category;
  companyId?: number;
  location?: string | null;
  postcode?: string | null;
  serviceId?: string | null;
  serviceVersionId?: string | null;
  forceLEQuestions?: boolean;
  enableRAQOption?: boolean;
  origin?: RequestOrigin;
  onExitModal?: () => void;
}>) => {
  const router = useRouter();
  const [address] = useLocalStorage<Address>('address');
  const [, setJobLocalStorage] = useLocalStorage('job', {});
  const { decide, loading: optimizelyLoading } = useOptimizely();

  const raqEntryPointEnabled = useMemo(() => {
    if (optimizelyLoading) return false;
    const decision = decide(Experiment.RAQ_ENTRY_POINT);
    return decision?.enabled && decision?.variationKey === 'new_raq_banner';
  }, [optimizelyLoading, decide]);

  const jobRefinementEnabled = useMemo(() => {
    if (optimizelyLoading) return false;
    const decision = decide(Experiment.LEAD_ENRICHMENT_FORM);
    return (
      decision?.enabled &&
      ['variant_a', 'variant_b', 'variant_c'].includes(
        decision?.variationKey ?? '',
      )
    );
  }, [optimizelyLoading, decide]);

  const [state, dispatch] = useReducer(jobRefinementReducer, {
    ...initialState,
    form,
    category,
    companyId,
    postcode,
    serviceId,
    serviceVersionId,
    ...(location ? { location } : {}),
    ...(origin ? { origin } : {}),
  });

  const handleSubmit = () => {
    if (origin !== 'pre-select') {
      track('job.message_submit', { categoryId: category?.id });

      const jobBlob = getTransformedJobBlob(state, address);
      setJobLocalStorage(jobBlob);

      return router.push(nextPage);
    }
  };

  useEffect(() => {
    if (typeof sessionStorage !== 'undefined') {
      const currentValue = sessionStorage?.getItem(
        getJobRefinementStorageName(form),
      );
      let parsedValue = {} as any;
      if (currentValue) {
        try {
          parsedValue = JSON.parse(currentValue);
        } catch (error) {}
      }

      if (Object.keys(parsedValue).length) {
        if (location === parsedValue.location) {
          return dispatch({
            type: INIT,
            payload: {
              ...parsedValue,
              category,
              postcode,
              showJobRefinementModal: !!forceLEQuestions,
              showSummary: !!forceLEQuestions && parsedValue.isFormCompleted,
              answeredQuestions: new Map(
                parsedValue.answeredQuestions as [string, AnsweredQuestion][],
              ),
            },
          });
        }

        const validForm = Object.keys(parsedValue.form).length > 0;
        return dispatch({
          type: INIT,
          payload: {
            ...parsedValue,
            category,
            ...(location ? { location } : {}),
            postcode,
            showJobRefinementModal:
              validForm &&
              !parsedValue.isFormCompleted &&
              (!!forceLEQuestions ||
                ['variant_b', 'variant_c'].includes(form.variant ?? '')),
            showSummary:
              !!forceLEQuestions &&
              parsedValue.isFormCompleted &&
              !raqEntryPointEnabled,
            answeredQuestions: new Map(
              parsedValue.answeredQuestions as [string, AnsweredQuestion][],
            ),
          },
        });
      }

      return dispatch({
        type: INIT,
        payload: {
          form,
          category,
          ...(location ? { location } : {}),
          postcode,
          showJobRefinementModal:
            !!forceLEQuestions ||
            ['variant_b', 'variant_c'].includes(form.variant ?? ''),
          showSummary: false,
          questionIndex: 0,
          isFormCompleted: false,
          answeredQuestions: new Map(),
          editMode: false,
        },
      });
    }
  }, [form.formId, category?.id, forceLEQuestions]);

  useEffect(() => {
    if (state.isFormCompleted && raqEntryPointEnabled) {
      handleSubmit();
    }
  }, [state.isFormCompleted]);

  useEffect(() => {
    const shouldSaveIfInRaqEntryPoint =
      raqEntryPointEnabled && !state.isFormCompleted;

    if (typeof sessionStorage !== 'undefined' && shouldSaveIfInRaqEntryPoint) {
      sessionStorage.setItem(
        getJobRefinementStorageName(state.form),
        JSON.stringify({
          ...state,
          answeredQuestions: Array.from(state.answeredQuestions.entries()),
        }),
      );
    }
  }, [state, form.categoryId]);

  return (
    <JobRefinementContext.Provider value={{ state, dispatch }}>
      {(raqEntryPointEnabled || jobRefinementEnabled) && (
        <>
          <JobRefinementModal
            disableDismiss={forceLEQuestions}
            isInRaqEntryPointMode={raqEntryPointEnabled}
            enableRAQOption={enableRAQOption}
            onExitModal={onExitModal}
          />
          {state.origin !== 'pre-select' && (
            <JobSummaryModal disableDismiss={forceLEQuestions} />
          )}
        </>
      )}
      {children}
    </JobRefinementContext.Provider>
  );
};
